import { useCallback, useMemo, useState } from 'react';
import { Button, Drawer } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { AdditionalDetailsStep, AdditionalDetailsStepSummary } from './components/additional-details-step/AdditionalDetailsStep';
import { HandlingUnitsStep, HandlingUnitsStepSummary } from './components/handling-units-step/HandlingUnitsStep';
import { ShipFromStep, ShipFromStepSummary } from './components/ship-from-step/ShipFromStep';
import { ShipToStep, ShipToStepSummary } from './components/ship-to-step/ShipToStep';
import { WizardRatesPanel } from './components/wizard-rates-panel/WizardRatesPanel';
import { WizardStep } from './components/wizard-step/WizardStep';
import { freightBookingWizardMessages } from './freight-booking-wizard-messages';
import styles from './FreightBookingWizard.module.css';
import {
  getStepErrors,
  getStepsThroughFirstInvalid,
  hasStepErrors,
  isReadyToBook,
  getIsReadyToQuote,
  WIZARD_STEP_IDS,
} from './wizard-step-validation';

import type { FreightBookingWizardProps, WizardStepId } from './freight-booking-wizard-types';

const EMPTY_FIELD_ERRORS: Record<string, string> = Object.freeze({});

export const FreightBookingWizard = ({
  isOpen,
  onClose,
  formState,
  onFormChange,
  shipFromOptions,
  states,
  displayWeightUnit,
  displayDimensionUnit,
  onCustomerSelect,
  onPasteAddressUsed,
  onAddShipFrom,
  onDensityAction,
  onCommodityPresetSelect,
  rates,
  isLoadingRates,
  rateError,
  rateErrorCode,
  selectedRate,
  onSelectRate,
  onRefresh,
  shipFromAddress,
  destinationAddress,
  onSaveDraft,
  onBook,
  isSavingDraft,
  isBooking,
  onDeleteDraft,
  isDeletingDraft,
  canDeleteDraft,
  initialCompletedSteps,
  initialActiveSteps,
  isHazmatEnabled,
  isFreightNotificationsEnabled,
  currencyCode = 'USD',
  onEditShipFrom,
  isSavingLtlDefaults,
  canSaveLtlDefaults,
  hasSavedLtlDefaults,
  zIndex,
}: FreightBookingWizardProps) => {
  const intl = useIntl();
  // Local UI-only state. Selection lives in the container (props), not here.
  // Initialized lazily from the optional pre-completion props (load-saved-quote).
  const [activeSteps, setActiveSteps] = useState<Set<WizardStepId>>(() => new Set(initialActiveSteps ?? (['ship-from'] as WizardStepId[])));
  const [completedSteps, setCompletedSteps] = useState<Partial<Record<WizardStepId, boolean>>>(() => initialCompletedSteps ?? {});
  // Seeds both sets from the same prefix: every step up to and including the first step with
  // validation errors (or all steps when the form is fully valid). This ensures that on open,
  // the first problem step shows its inline required-field errors immediately, steps before it
  // are silently collapsed, and steps after it stay pending and silent.
  const [visitedSteps, setVisitedSteps] = useState<Set<WizardStepId>>(() => new Set(getStepsThroughFirstInvalid(formState)));
  // Tracks steps that have been collapsed at least once via the caret button. Inline field errors
  // are shown only when a step is in this set AND is currently active (the user collapsed or
  // confirmed it, then reopened it — or it was seeded as visited on open).
  const [collapsedSteps, setCollapsedSteps] = useState<Set<WizardStepId>>(() => new Set(getStepsThroughFirstInvalid(formState)));

  const handleCompleteStep = useCallback((step: WizardStepId) => {
    setCompletedSteps((prev) => ({ ...prev, [step]: true }));
    setActiveSteps((prev) => {
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
    setCollapsedSteps((prev) => new Set([...prev, step]));
    setVisitedSteps((prev) => new Set([...prev, step]));
  }, []);

  const handleEditStep = useCallback((step: WizardStepId) => {
    setVisitedSteps((prev) => new Set([...prev, step]));
    setActiveSteps((prev) => new Set([...prev, step]));
  }, []);

  const isWarningStep = (step: WizardStepId) => visitedSteps.has(step) && !activeSteps.has(step) && hasStepErrors(step, formState);

  const canBook =
    isReadyToBook(formState, { selectedRate }) && Boolean(selectedRate?.productTransactionId) && !isLoadingRates && !rateError;

  // Missing-required errors are shown only once the step has been collapsed at least once
  // and is currently expanded (the user has opened it a second time), so an untouched form
  // isn't covered in red. Errors flagged isInvalidValue bypass that wait: they can arrive
  // from a hydrated saved quote the user never typed, and they block quoting, so staying
  // silent would leave the form dead with no explanation. Memoized so children only
  // re-render when formState, collapsedSteps, or activeSteps changes.
  const fieldErrorsByStep = useMemo(
    () =>
      Object.fromEntries(
        WIZARD_STEP_IDS.map((step) => {
          const shouldShowMissingFields = collapsedSteps.has(step) && activeSteps.has(step);
          const errors = Object.fromEntries(
            getStepErrors(step, formState)
              .filter((e) => shouldShowMissingFields || e.isInvalidValue)
              .map((e) => [e.path, intl.formatMessage(e.message, e.values)]),
          );
          return [step, Object.keys(errors).length > 0 ? errors : EMPTY_FIELD_ERRORS];
        }),
      ) as Record<WizardStepId, Record<string, string>>,
    [formState, collapsedSteps, activeSteps, intl],
  );

  const stepProps = { formState, onFormChange };

  return (
    <Drawer
      classNames={{ body: styles.drawerBodySlot, content: styles.drawerContent, header: styles.header }}
      onClose={onClose}
      opened={isOpen}
      padding={0}
      position='right'
      size='100%'
      title={intl.formatMessage(freightBookingWizardMessages.title)}
      zIndex={zIndex}
    >
      <div className={styles.drawerBody}>
        <div className={styles.stepsColumn}>
          <div className={styles.steps}>
            <WizardStep
              isActive={activeSteps.has('ship-from')}
              isCompleted={Boolean(completedSteps['ship-from'])}
              isWarning={isWarningStep('ship-from')}
              onCollapse={() => handleCompleteStep('ship-from')}
              onEdit={() => handleEditStep('ship-from')}
              stepNumber={1}
              summary={
                <ShipFromStepSummary
                  formState={formState}
                  pickupCloseTime={formState.pickupCloseTime}
                  pickupReadyTime={formState.pickupReadyTime}
                  shipFromOptions={shipFromOptions}
                />
              }
              title={intl.formatMessage(freightBookingWizardMessages.shipFromStepTitle)}
            >
              <ShipFromStep
                {...stepProps}
                canSaveLtlDefaults={canSaveLtlDefaults}
                fieldErrors={fieldErrorsByStep['ship-from']}
                hasSavedLtlDefaults={hasSavedLtlDefaults}
                isSavingLtlDefaults={isSavingLtlDefaults}
                onAddShipFrom={onAddShipFrom}
                onEditShipFrom={onEditShipFrom}
                shipFromOptions={shipFromOptions}
              />
            </WizardStep>
            <WizardStep
              isActive={activeSteps.has('ship-to')}
              isCompleted={Boolean(completedSteps['ship-to'])}
              isWarning={isWarningStep('ship-to')}
              onCollapse={() => handleCompleteStep('ship-to')}
              onEdit={() => handleEditStep('ship-to')}
              stepNumber={2}
              summary={<ShipToStepSummary formState={formState} />}
              title={intl.formatMessage(freightBookingWizardMessages.shipToStepTitle)}
            >
              <ShipToStep
                {...stepProps}
                fieldErrors={fieldErrorsByStep['ship-to']}
                onCustomerSelect={onCustomerSelect}
                onPasteAddressUsed={onPasteAddressUsed}
                states={states}
              />
            </WizardStep>
            <WizardStep
              isActive={activeSteps.has('handling-units')}
              isCompleted={Boolean(completedSteps['handling-units'])}
              isWarning={isWarningStep('handling-units')}
              onCollapse={() => handleCompleteStep('handling-units')}
              onEdit={() => handleEditStep('handling-units')}
              stepNumber={3}
              summary={<HandlingUnitsStepSummary displayWeightUnit={displayWeightUnit} formState={formState} />}
              title={intl.formatMessage(freightBookingWizardMessages.handlingUnitsStepTitle)}
            >
              <HandlingUnitsStep
                {...stepProps}
                displayDimensionUnit={displayDimensionUnit}
                displayWeightUnit={displayWeightUnit}
                fieldErrors={fieldErrorsByStep['handling-units']}
                isHazmatEnabled={isHazmatEnabled}
                onCommodityPresetSelect={onCommodityPresetSelect}
                onDensityAction={onDensityAction}
                zIndex={zIndex !== undefined ? zIndex + 1 : undefined}
              />
            </WizardStep>
            <WizardStep
              isActive={activeSteps.has('additional-details')}
              isCompleted={Boolean(completedSteps['additional-details'])}
              isWarning={isWarningStep('additional-details')}
              onCollapse={() => handleCompleteStep('additional-details')}
              onEdit={() => handleEditStep('additional-details')}
              stepNumber={4}
              summary={<AdditionalDetailsStepSummary currencyCode={currencyCode} formState={formState} />}
              title={intl.formatMessage(freightBookingWizardMessages.additionalDetailsStepTitle)}
            >
              <AdditionalDetailsStep {...stepProps} isFreightNotificationsEnabled={isFreightNotificationsEnabled} />
            </WizardStep>
          </div>
        </div>
        <div className={styles.ratesColumn}>
          <WizardRatesPanel
            isReadyToQuote={getIsReadyToQuote(formState)}
            destinationAddress={destinationAddress}
            insurance={formState.insurance}
            isLoadingRates={isLoadingRates}
            onInsuranceChange={(next) => onFormChange({ ...formState, insurance: next })}
            onRefresh={onRefresh}
            onSelectRate={onSelectRate}
            rateError={rateError}
            rateErrorCode={rateErrorCode}
            rates={rates}
            selectedRate={selectedRate}
            shipFromAddress={shipFromAddress}
            zIndex={zIndex !== undefined ? zIndex + 1 : undefined}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button onClick={onClose} variant='ghost'>
          <FormattedMessage {...freightBookingWizardMessages.cancel} />
        </Button>
        <div className={styles.footerRight}>
          {canDeleteDraft && (
            <Button color='destructive' disabled={isDeletingDraft} loading={isDeletingDraft} onClick={onDeleteDraft}>
              <FormattedMessage {...freightBookingWizardMessages.deleteDraft} />
            </Button>
          )}
          <Button disabled={isSavingDraft} loading={isSavingDraft} onClick={onSaveDraft} variant='tertiary'>
            <FormattedMessage {...freightBookingWizardMessages.saveDraft} />
          </Button>
          <Button color='green' disabled={!canBook} loading={isBooking} onClick={onBook}>
            <FormattedMessage {...freightBookingWizardMessages.bookShipment} />
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
